import React, { useState, useCallback } from 'react'
import { JSBI, Pair, Percent } from 'vvs-sdk'
import {
  Button,
  Text,
  ChevronUpIcon,
  ChevronDownIcon,
  Card,
  CardBody,
  Flex,
  CardProps,
  AddIcon,
  Box,
  useModal,
} from 'vvs-uikit'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { useTranslation } from 'contexts/Localization'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { isSmartRemovalEnabled } from 'utils'
import useSmartApr from 'views/RemoveLiquidity/hooks/useSmartApr'
import { useFarms, usePriceVvsUsdc, useLpTokenPrice, useTokenPricesUsdc } from 'state/farms/hooks'
import { TokenPairImage } from 'components/TokenImage'
import BigNumber from 'bignumber.js'
import { BIG_ZERO } from 'utils/bigNumber'
import { getFarmApr, getFarmAprV2 } from 'utils/apr'
import { getAddress } from 'utils/addressHelpers'
import { getDisplayApr } from 'views/Farms/Farms'
import getLiquidityUrlPathParts from 'utils/getLiquidityUrlPathParts'
import { fetchFarmUserDataAsync } from 'state/farms'
import { useAppDispatch } from 'state'
import { BASE_ADD_LIQUIDITY_URL } from 'config'
import { getCrc20Contract } from 'utils/contractHelpers'
import useApproveFarm from 'views/Farms/hooks/useApproveFarm'
import useStakeFarms from 'views/Farms/hooks/useStakeFarms'
import useStakeFarmsV2 from 'views/Farms/hooks/useStakeFarmsV2'
import useApproveFarmV2 from 'views/Farms/hooks/useApproveFarmV2'

import DepositModal from '../../views/Farms/components/DepositModal'
import useTotalSupply from '../../hooks/useTotalSupply'
import { useTokenBalance } from '../../state/wallet/hooks'
import { currencyId } from '../../utils/currencyId'
import { unwrappedToken } from '../../utils/wrappedCurrency'

import { LightCard } from '../Card'
import { AutoColumn } from '../Layout/Column'
import CurrencyLogo from '../Logo/CurrencyLogo'
import { DoubleCurrencyLogo } from '../Logo'
import { RowBetween, RowFixed } from '../Layout/Row'
import { BIG_INT_ZERO } from '../../config/constants'
import Dots from '../Loader/Dots'

const Divider = styled.div`
  background-color: ${({ theme }) => theme.colors.cardBorder};
  height: 1px;
  margin: 20px 0;
  width: 100%;
`

const TokenWrapper = styled.div`
  top: 4px;
  position: relative;

  ${({ theme }) => theme.mediaQueries.sm} {
    width: 60px;
  }
`

const FixedHeightRow = styled(RowBetween)`
  height: 24px;
`

interface PositionCardProps extends CardProps {
  pair: Pair
  showUnwrapped?: boolean
  source?: string
}

export function MinimalPositionCard({ pair, showUnwrapped = false, source }: PositionCardProps) {
  const { account, library } = useActiveWeb3React()

  const { t } = useTranslation()

  const currency0 = showUnwrapped ? pair.token0 : unwrappedToken(pair.token0)
  const currency1 = showUnwrapped ? pair.token1 : unwrappedToken(pair.token1)

  const [requestedApproval, setRequestedApproval] = useState(false)
  const vvsPrice = usePriceVvsUsdc()
  const { data: farmsLp } = useFarms()
  const tokenPrices = useTokenPricesUsdc(farmsLp)
  const farm = farmsLp.find((f) => {
    return (
      (f.quoteToken.address === pair.token1.address && f.token.address === pair.token0.address) ||
      (f.quoteToken.address === pair.token0.address && f.token.address === pair.token1.address)
    )
  })
  const dispatch = useAppDispatch()

  let apr = null
  let lpApr = null
  let addLiquidityUrl = null
  let displayApr = null
  let totalLiquidity = null
  if (farm) {
    totalLiquidity = new BigNumber(farm.lpTotalInQuoteToken).times(farm.quoteTokenPriceUsdc)
    if (farm.version === 2) {
      const { tokenRewardsApr: tapr, lpRewardsApr: lapr } = getFarmAprV2(
        farm.poolWeight,
        vvsPrice,
        farm.rewardData,
        totalLiquidity,
        getAddress(farm.lpAddresses),
        tokenPrices,
      )
      apr = tapr
      lpApr = lapr
    } else {
      const { vvsRewardsApr: tapr, lpRewardsApr: lapr } = getFarmApr(
        farm.poolWeight,
        vvsPrice,
        totalLiquidity,
        getAddress(farm.lpAddresses),
      )
      apr = tapr
      lpApr = lapr
    }
    const liquidityUrlPathParts = getLiquidityUrlPathParts({
      quoteTokenAddress: farm.quoteToken.address,
      tokenAddress: farm.token.address,
    })
    displayApr = getDisplayApr(apr, lpApr)
    addLiquidityUrl = `${BASE_ADD_LIQUIDITY_URL}/${liquidityUrlPathParts}`
  }
  const pid = farm?.pid
  const lpLabel = farm?.lpSymbol && farm?.lpSymbol.toUpperCase()
  const tokenName = farm?.lpSymbol
  const lpPrice = useLpTokenPrice(tokenName ?? 'VVS')
  const token = farm?.token
  const quoteToken = farm?.quoteToken
  const { allowance, stakedBalance } = farm?.userData || {}
  const isApproved = account && allowance && allowance.isGreaterThan(0)
  const lpContract = farm && getCrc20Contract(getAddress(farm.lpAddresses), library.getSigner())
  const { onApprove } = useApproveFarm(lpContract)
  const { onApprove: onApproveV2 } = useApproveFarmV2(lpContract)
  const { onStake } = useStakeFarms(farm?.pid)
  const { onStake: onStakeV2 } = useStakeFarmsV2(pid)

  const handleStake = async (amount: string) => {
    if (farm.version === 2) {
      await onStakeV2(amount)
    } else {
      await onStake(amount)
    }
    dispatch(fetchFarmUserDataAsync({ account, pids: [pid] }))
  }

  const handleApprove = useCallback(async () => {
    try {
      const { version } = farm
      setRequestedApproval(true)
      if (version === 2) {
        await onApproveV2()
      } else {
        await onApprove()
      }
      dispatch(fetchFarmUserDataAsync({ account, pids: [pid] }))

      setRequestedApproval(false)
    } catch (e) {
      console.error(e)
    }
  }, [onApprove, onApproveV2, dispatch, account, pid, farm])

  const [showMore, setShowMore] = useState(false)

  const userPoolBalance = useTokenBalance(account ?? undefined, pair.liquidityToken)
  const totalPoolTokens = useTotalSupply(pair.liquidityToken)

  const [onPresentDeposit] = useModal(
    <DepositModal
      max={userPoolBalance?.raw?.toString() ? new BigNumber(userPoolBalance?.raw?.toString()) : BIG_ZERO}
      stakedBalance={stakedBalance}
      onConfirm={handleStake}
      tokenName={tokenName}
      lpPrice={lpPrice}
      lpLabel={lpLabel}
      apr={apr}
      displayApr={displayApr}
      addLiquidityUrl={addLiquidityUrl}
      vvsPrice={vvsPrice}
      token={token}
      quoteToken={quoteToken}
      source={source}
      poolLiquidity={totalLiquidity}
      poolWeight={farm?.poolWeight}
      rewardData={farm?.rewardData}
      farms={farmsLp}
    />,
  )

  const poolTokenPercentage =
    !!userPoolBalance && !!totalPoolTokens && JSBI.greaterThanOrEqual(totalPoolTokens.raw, userPoolBalance.raw)
      ? new Percent(userPoolBalance.raw, totalPoolTokens.raw)
      : undefined

  const [token0Deposited, token1Deposited] =
    !!pair &&
    !!totalPoolTokens &&
    JSBI.notEqual(totalPoolTokens.raw, JSBI.BigInt(0)) &&
    !!userPoolBalance &&
    JSBI.notEqual(userPoolBalance.raw, JSBI.BigInt(0)) &&
    // this condition is a short-circuit in the case where useTokenBalance updates sooner than useTotalSupply
    JSBI.greaterThanOrEqual(totalPoolTokens.raw, userPoolBalance.raw)
      ? [
          pair.getLiquidityValue(pair.token0, totalPoolTokens, userPoolBalance, false),
          pair.getLiquidityValue(pair.token1, totalPoolTokens, userPoolBalance, false),
        ]
      : [undefined, undefined]

  return (
    <>
      {userPoolBalance && JSBI.greaterThan(userPoolBalance.raw, JSBI.BigInt(0)) ? (
        <Card>
          <CardBody>
            <AutoColumn gap="16px">
              <FixedHeightRow>
                <RowFixed>
                  <Text color="text" bold>
                    {t('LP tokens in your wallet')}
                  </Text>
                </RowFixed>
              </FixedHeightRow>
              <FixedHeightRow onClick={() => setShowMore(!showMore)}>
                <RowFixed>
                  <DoubleCurrencyLogo currency0={currency0} currency1={currency1} margin size={20} />
                  <Text small color="textSubtle">
                    {currency0.symbol}-{currency1.symbol} LP
                  </Text>
                </RowFixed>
                <RowFixed>
                  <Text>{userPoolBalance ? userPoolBalance.toSignificant(4) : '-'}</Text>
                </RowFixed>
              </FixedHeightRow>
              <AutoColumn gap="4px">
                <FixedHeightRow>
                  <Text color="textSubtle" small>
                    {t('Share of Pool')}:
                  </Text>
                  <Text>{poolTokenPercentage ? `${poolTokenPercentage.toFixed(6)}%` : '-'}</Text>
                </FixedHeightRow>
                <FixedHeightRow>
                  <Text color="textSubtle" small>
                    {t('Pooled %asset%', { asset: currency0.symbol })}:
                  </Text>
                  {token0Deposited ? (
                    <RowFixed>
                      <Text ml="6px">{token0Deposited?.toSignificant(6)}</Text>
                    </RowFixed>
                  ) : (
                    '-'
                  )}
                </FixedHeightRow>
                <FixedHeightRow>
                  <Text color="textSubtle" small>
                    {t('Pooled %asset%', { asset: currency1.symbol })}:
                  </Text>
                  {token1Deposited ? (
                    <RowFixed>
                      <Text ml="6px">{token1Deposited?.toSignificant(6)}</Text>
                    </RowFixed>
                  ) : (
                    '-'
                  )}
                </FixedHeightRow>
              </AutoColumn>
            </AutoColumn>
            {farm && !farm.isFinished && (
              <>
                <Divider />
                <Flex flexDirection="row" justifyContent="space-between">
                  <Flex flexDirection="column">
                    <Flex flexDirection="row" alignItems="center">
                      <TokenWrapper>
                        <TokenPairImage
                          variant="inverted"
                          primaryToken={pair.token0}
                          secondaryToken={pair.token1}
                          width={32}
                          height={32}
                        />
                      </TokenWrapper>
                      <Box ml="-10px">
                        <Text small color="textSubtle">
                          {`${currency0.symbol}-${currency1.symbol}`} Crystal Farm
                        </Text>
                      </Box>
                    </Flex>
                    <Box mt="5px">
                      <Text color="bold" small>
                        {`APR: ${displayApr ?? 0}%`}
                      </Text>
                    </Box>
                  </Flex>
                  {isApproved ? (
                    <Button variant="secondary" onClick={onPresentDeposit}>
                      <Text color="#46A1C2" bold as="h3">
                        Stake in Farm
                      </Text>
                    </Button>
                  ) : (
                    <Button disabled={requestedApproval} onClick={handleApprove} variant="secondary">
                      {t('Enable')}
                    </Button>
                  )}
                </Flex>
              </>
            )}
          </CardBody>
        </Card>
      ) : (
        <LightCard>
          <Text fontSize="13px" color="darkGrey" fontWeight="400">
            {t(
              "By adding liquidity you'll earn 0.20% of all trades on this pair proportional to your share of the pool. Fees are added to the pool, accrue in real time and can be claimed by withdrawing your liquidity.",
            )}
          </Text>
        </LightCard>
      )}
    </>
  )
}

export default function FullPositionCard({ pair, ...props }: PositionCardProps) {
  const { account } = useActiveWeb3React()

  const { targetFarm: existingFarm } = useSmartApr(pair)

  const currency0 = unwrappedToken(pair.token0)
  const currency1 = unwrappedToken(pair.token1)

  const [showMore, setShowMore] = useState(false)

  const userPoolBalance = useTokenBalance(account ?? undefined, pair.liquidityToken)
  const totalPoolTokens = useTotalSupply(pair.liquidityToken)

  const poolTokenPercentage =
    !!userPoolBalance && !!totalPoolTokens && JSBI.greaterThanOrEqual(totalPoolTokens.raw, userPoolBalance.raw)
      ? new Percent(userPoolBalance.raw, totalPoolTokens.raw)
      : undefined

  const [token0Deposited, token1Deposited] =
    !!pair &&
    !!totalPoolTokens &&
    !!userPoolBalance &&
    // this condition is a short-circuit in the case where useTokenBalance updates sooner than useTotalSupply
    JSBI.greaterThanOrEqual(totalPoolTokens.raw, userPoolBalance.raw)
      ? [
          pair.getLiquidityValue(pair.token0, totalPoolTokens, userPoolBalance, false),
          pair.getLiquidityValue(pair.token1, totalPoolTokens, userPoolBalance, false),
        ]
      : [undefined, undefined]

  return (
    <Card p={0} {...props}>
      <Flex justifyContent="space-between" role="button" onClick={() => setShowMore(!showMore)} p="16px">
        <Flex flexDirection="column">
          <Flex alignItems="center" mb="4px">
            <DoubleCurrencyLogo currency0={currency0} currency1={currency1} size={20} />
            <Text bold ml="8px">
              {!currency0 || !currency1 ? <Dots>Loading</Dots> : `${currency0.symbol}/${currency1.symbol}`}
            </Text>
          </Flex>
          <Text fontSize="14px" color="textSubtle">
            {userPoolBalance?.toSignificant(4)}
          </Text>
        </Flex>
        {showMore ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </Flex>

      {showMore && (
        <AutoColumn gap="8px" style={{ padding: '16px' }}>
          <FixedHeightRow>
            <RowFixed>
              <CurrencyLogo size="20px" currency={currency0} />
              <Text color="textSubtle" ml="4px">
                Pooled {currency0.symbol}
              </Text>
            </RowFixed>
            {token0Deposited ? (
              <RowFixed>
                <Text ml="6px">{token0Deposited?.toSignificant(6)}</Text>
              </RowFixed>
            ) : (
              '-'
            )}
          </FixedHeightRow>

          <FixedHeightRow>
            <RowFixed>
              <CurrencyLogo size="20px" currency={currency1} />
              <Text color="textSubtle" ml="4px">
                Pooled {currency1.symbol}
              </Text>
            </RowFixed>
            {token1Deposited ? (
              <RowFixed>
                <Text ml="6px">{token1Deposited?.toSignificant(6)}</Text>
              </RowFixed>
            ) : (
              '-'
            )}
          </FixedHeightRow>

          <FixedHeightRow>
            <Text color="textSubtle">Share of pool</Text>
            <Text>
              {poolTokenPercentage
                ? `${poolTokenPercentage.toFixed(2) === '0.00' ? '<0.01' : poolTokenPercentage.toFixed(2)}%`
                : '-'}
            </Text>
          </FixedHeightRow>

          {userPoolBalance && JSBI.greaterThan(userPoolBalance.raw, BIG_INT_ZERO) && (
            <Flex flexDirection="column">
              <Button
                as={Link}
                to={
                  isSmartRemovalEnabled() && existingFarm
                    ? `/remove/${currencyId(currency0)}/${currencyId(currency1)}`
                    : `/remove/instant/${currencyId(currency0)}/${currencyId(currency1)}`
                }
                variant="primary"
                width="100%"
                mb="8px"
              >
                Remove
              </Button>
              <Button
                as={Link}
                to={`/add/${currencyId(currency0)}/${currencyId(currency1)}`}
                variant="text"
                startIcon={<AddIcon color="primary" />}
                width="100%"
              >
                Supply More
              </Button>
            </Flex>
          )}
        </AutoColumn>
      )}
    </Card>
  )
}
